import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { FormCheckboxType } from '#pie/components/form/types';
import type { CreateSimpleDataRequest } from '#pie/internal/api/pie-bff';
import { Period } from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';

const { ConditionalField, FormCheckbox, FormText } =
  typedFormFields<CreateSimpleDataRequest>();

export const SimplePeriods = () => {
  // Over welke periode wil je data ontvangen?
  const { t } = useTranslation('dataRequest');
  const currentYear = new Date().getFullYear();
  const dynamicYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const options: FormCheckboxType[] = [
    {
      label: t(`periods.${Period.Past5Years}`),
      value: Period.Past5Years,
    },
    ...dynamicYears.map((year) => {
      const periodKey = `_${year}` as keyof typeof Period;
      return {
        label: t('periods.year', { year: year }),
        value: Period[periodKey],
      };
    }),
    {
      label: t(`periods.${Period.Other}`),
      renderInput: (
        <ConditionalField
          field="periodNote"
          condition={(fieldValue) =>
            !!fieldValue('periods') &&
            fieldValue('periods')!.includes(Period.Other)
          }
        >
          <FormText
            fullWidth
            name="periodNote"
            rules={{ maxLength: 25, minLength: 2, required: true }}
          />
        </ConditionalField>
      ),
      value: Period.Other,
    },
  ];

  const { watch, setValue } = useFormContext<CreateSimpleDataRequest>();
  const gridOperatorData = watch('periods');
  const dataTypes = watch('dataTypes');

  useEffect(() => {
    if (dataTypes && dataTypes.length > 0) {
      if (
        gridOperatorData?.slice(-1)[0] === Period.Past5Years &&
        gridOperatorData.length > 1
      ) {
        setValue('periods', [Period.Past5Years]);
      } else if (
        gridOperatorData?.includes(Period.Past5Years) &&
        gridOperatorData.length > 1
      ) {
        setValue(
          'periods',
          gridOperatorData?.filter((value) => value !== Period.Past5Years),
        );
      }
    }
  }, [gridOperatorData, dataTypes, setValue]);

  return (
    <ConditionalField
      field="periods"
      condition={(formValue) => {
        return !!formValue('dataTypes') && formValue('dataTypes')!.length > 0;
      }}
    >
      <FormCheckbox
        infoTooltip={t('periods.tooltip')}
        label={t('periods')}
        name="periods"
        options={options}
        rules={{ required: true }}
      />
    </ConditionalField>
  );
};
